import type { EmployeeSchedules } from "@/pages/human-resources/time-management/business/EmployeeSchedules";
import type { Schedule } from "@/pages/human-resources/time-management/business/schedule/Schedule";
import useVisibleEmployeesSchedules from "@/pages/human-resources/time-management/model/schedule-view/useVisibleEmployeesSchedules";
import useDateFormatter from "@/shared/custom-hook/useDateFormatter";
import type { ReactNode } from "react";
import { DateStyle } from "@/shared/utils/Dates.ts";
import "./LastScheduleModification.scss";
import IconClockSmallThick from "@/shared/components/generic/icons/IconClockSmallThick.tsx";
import IconCheckMark from "@/shared/components/generic/icons/IconCheckMark.tsx";
import { useTranslation } from "react-i18next";

export default function LastScheduleModification(): ReactNode {
  const { t } = useTranslation("humanResources", {
    keyPrefix: "timeManagement.metaData",
  });
  const { formatDate } = useDateFormatter();
  const schedules = useVisibleEmployeesSchedules();
  const lastModifiedSchedule = findLastModified(schedules);
  const metaModified = lastModifiedSchedule?.metaData?.modified;
  if (lastModifiedSchedule === undefined || metaModified === undefined)
    return undefined;

  return (
    <div className="last-schedule-modification">
      {lastModifiedSchedule.modified ?
        <IconClockSmallThick />
      : <IconCheckMark />}
      <div>
        {lastModifiedSchedule.modified ?
          t("lastModification")
        : t("validatedBy")}
        {" : "}
        <strong>{metaModified.username}&nbsp;•&nbsp;</strong>
        {formatDate(
          metaModified.date,
          DateStyle.DATE_MONTH_WORD_SHORT_YEAR,
        )}{" "}
        {formatDate(metaModified.date, DateStyle.TIME)}
      </div>
    </div>
  );
}

function findLastModified(es: EmployeeSchedules[]): Schedule | undefined {
  const timeStampOf = (s: Schedule | undefined): number =>
    s?.metaData?.modified.date.getTime() ?? 0;
  let res: Schedule | undefined;
  for (const schedule of es.flatMap((es) => es.getMainSchedules())) {
    if (timeStampOf(schedule) > timeStampOf(res)) res = schedule;
  }
  return res;
}
